import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  deletePhoneConfig,
  listPhoneConfigs,
  upsertPhoneConfig,
} from '@/api/phoneConfig';
import PhoneConfigForm from '@/components/phoneConfig/PhoneConfigForm';
import DeletePhoneConfigDialog from '@/components/phoneConfig/DeletePhoneConfigDialog';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { PhoneConfigItem, PhoneConfigUpsert } from '@/types/api';

export default function PhoneConfigPage() {
  const { t } = useTranslation(['phone', 'common']);
  const queryClient = useQueryClient();
  const listQuery = useQuery({ queryKey: ['phone-config'], queryFn: listPhoneConfigs });
  const [editing, setEditing] = useState<PhoneConfigItem | 'new' | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const upsertMutation = useMutation({
    mutationFn: ({ phone, body }: { phone: string; body: PhoneConfigUpsert }) =>
      upsertPhoneConfig(phone, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['phone-config'] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePhoneConfig,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['phone-config'] });
      setDeleting(null);
    },
  });

  const items = listQuery.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">{t('title')}</h2>
        <Button size="sm" onClick={() => setEditing('new')}>
          {t('addNew')}
        </Button>
      </div>

      <div className="overflow-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-2 py-1 text-left font-medium">{t('table.phone')}</th>
              <th className="px-2 py-1 text-left font-medium">{t('table.tasks')}</th>
              <th className="px-2 py-1 text-left font-medium">{t('table.language')}</th>
              <th className="px-2 py-1 text-left font-medium">{t('table.note')}</th>
              <th className="px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-3 text-muted-foreground">
                  {t('table.empty')}
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.phone_number} className="border-t">
                  <td className="px-2 py-1">{it.phone_number}</td>
                  <td className="px-2 py-1">{it.tasks}</td>
                  <td className="px-2 py-1">{it.language ?? '—'}</td>
                  <td className="px-2 py-1">{it.note ?? ''}</td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setEditing(it)}
                    >
                      {t('common:actions.edit')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-destructive"
                      onClick={() => setDeleting(it.phone_number)}
                    >
                      {t('common:actions.delete')}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Sheet open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent className="overflow-auto">
          <SheetHeader>
            <SheetTitle>{t('form.title')}</SheetTitle>
          </SheetHeader>
          {editing && (
            <PhoneConfigForm
              initial={editing === 'new' ? undefined : editing}
              onSubmit={(phone, body) => upsertMutation.mutate({ phone, body })}
              onCancel={() => setEditing(null)}
              pending={upsertMutation.isPending}
            />
          )}
        </SheetContent>
      </Sheet>

      <DeletePhoneConfigDialog
        phone={deleting}
        open={deleting !== null}
        onOpenChange={(v) => !v && setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
        pending={deleteMutation.isPending}
      />
    </div>
  );
}
